/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import Table, { TableColumn } from '../../components/table/Table';
import {
  addMetricToGroup,
  createMetricGroup,
  removeMetricFromGroup
} from '../../actions/initiative';
import { UniversalTrackerPlain } from '../../types/universalTracker';
import { getGroup, standards } from '@g17eco/core';
import SimpleTooltip from '../../components/simple-tooltip';
import { loadUniversalTrackerModal } from '../../actions/universalTrackerModal';
import Loader from '../../components/loader';
import { NameCell } from './partials/NameCell';
import './styles.scss'
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import IconButton from '../button/IconButton';
import { FormProps } from './MetricGroupForm';
import { CustomMetricContext } from './CustomMetricContainer';
import { getCurrentUser } from '../../selectors/user';
import { useAppSelector } from '../../reducers';
import { InitiativeTree } from '../../utils/initiative';
import { CurrentUserData } from '../../reducers/current-user';
import { QUESTION } from '../../constants/terminology';

export interface MetricTableProps extends FormProps {
  isReadOnly?: boolean;
  questions: UniversalTrackerPlain[];
  handleEditUtr?: (utrId: string) => void;
  helperText?: string;
}

interface MenuButtonProps {
  user?: CurrentUserData;
  initiativeId: string;
  universalTracker: UniversalTrackerPlain;
  handleEditUtr: (utrId: string) => void;
  handleRemoveUtr: (utrId: string) => void;
}

const CTAButtons = ({ universalTracker, user, handleEditUtr, initiativeId, handleRemoveUtr }: MenuButtonProps) => {
  const dispatch = useDispatch();

  const utrId = universalTracker._id;
  const isEditable = universalTracker.type === 'custom_kpi';
  const ownerId = universalTracker.ownerId;
  const hasPermission = user && (!ownerId || InitiativeTree.isDescendant(user, initiativeId, ownerId));

  if (!hasPermission) {
    return null;
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      <SimpleTooltip text='Edit details'>
        <IconButton
          icon='fal fa-pen-to-square'
          color='transparent'
          size='xs'
          className='icon-button--size-xs'
          outline={false}
          onClick={() => handleEditUtr(utrId)}
          disabled={!isEditable}
        />
      </SimpleTooltip>
      <SimpleTooltip text='Open data history'>
        <IconButton
          icon='fal fa-clock-rotate-left'
          color='transparent'
          size='xs'
          className='icon-button--size-xs'
          outline={false}
          onClick={() => dispatch(loadUniversalTrackerModal({ universalTrackerId: utrId }))}
        />
      </SimpleTooltip>
      <SimpleTooltip text='Remove from group'>
        <IconButton
          icon='fal fa-times'
          color='secondary'
          size='xs'
          className='icon-button--size-xs'
          onClick={() => handleRemoveUtr(utrId)}
        />
      </SimpleTooltip>
    </div>
  );
}

const MetricTable = ({ isReadOnly = false, metricGroup, handleReload, questions, handleEditUtr, helperText }: MetricTableProps) => {
  const { initiativeId } = React.useContext(CustomMetricContext);

  const dispatch = useDispatch();
  const user = useAppSelector(getCurrentUser);

  const [errorMessage, setMessage] = React.useState();
  const [isSaving, setSaving] = React.useState(false);

  const isUTRInGroup = (utrId: string) => metricGroup?.universalTrackers?.includes(utrId) ?? false;

  const getBadge = (text: string, tooltip: string, badgeColour: string, key: string) => {
    return (
      <SimpleTooltip text={tooltip} key={key}>
        <div className={`badge ${badgeColour} mr-1`}>{text}</div>
      </SimpleTooltip>
    );
  };

  const addToGroup = async (row: UniversalTrackerPlain) => {
    if (isSaving) {
      return;
    }
    setSaving(true);
    try {
      let groupId;
      if (metricGroup?._id) {
        groupId = metricGroup._id;
      } else {
        const groupData = await createMetricGroup(initiativeId, metricGroup);
        groupId = groupData._id;
      }

      if (!groupId) {
        throw Error('No valid Metric Group');
      }
      const utrId = row._id
      const type = row.type
      const utrCode = row.code
      await addMetricToGroup(initiativeId, groupId, utrId);
      getAnalytics().track(AnalyticsEvents.CustomMetricAdded, {initiativeId, groupId, utrId, type, utrCode})
      dispatch(addSiteAlert({
        content: <><strong>Success!</strong> Custom metric has been added</>,
        color: SiteAlertColors.Primary,
        timeout: 3000
      }));
      handleReload();
    }
    catch (e) {
      setMessage(e.message);
      handleReload();
    }
    setSaving(false);
  }

  const removeFromGroup = async (utrId: string) => {
    if (isSaving) {
      return;
    }
    setSaving(true);
    try {
      if (!metricGroup?._id) {
        throw Error('No valid Metric Group');
      }

      await removeMetricFromGroup(initiativeId, metricGroup._id, utrId);
      handleReload();
      dispatch(addSiteAlert({
        content: <><strong>Success!</strong> Custom metric has been removed</>,
        color: SiteAlertColors.Primary,
        timeout: 3000
      }));
    }
    catch (e) {
      setMessage(e.message);
    }
    setSaving(false);
  }

  const tableColumns: TableColumn<UniversalTrackerPlain>[] = [];

  tableColumns.push({
    accessor: 'name',
    Header: () => `${QUESTION.CAPITALIZED_SINGULAR} name`,
    cellProps: {
      className: 'nameCol flex-grow-1 order-1'
    },
    Cell: ({ row }) => <NameCell isInGroup={isUTRInGroup(row.original._id)} row={row} />
  });

  tableColumns.push({
    accessor: 'type',
    Header: () => 'Standards',
    cellProps: {
      className: 'typeCol row-break-1 row-lg-break-0 order-2',
    },
    Cell: ({ row }) => {
      const type = row.values.type;
      const utrId = row.original._id;
      let typeStr = type;
      if (type === 'custom_kpi') {
        typeStr = 'Custom Metric';
        const ownerId = row.original.ownerId;
        const hasPermission = user && (!ownerId || InitiativeTree.isDescendant(user, initiativeId, ownerId));
        if (!hasPermission) {
          typeStr = 'Assigned Metric';
        }
      }
      if (type === 'metric') {
        typeStr = 'Standardized Metric';
      }
      const selectedBadgeColour = type === 'custom_kpi' ? 'background-ThemeSuccessDark' : 'background-ThemeNeutralsExtradark';
      const unselectedBadgeColour = type === 'custom_kpi' ? 'background-ThemeSuccessMedium' : 'background-ThemeNeutralsDark';
      const badgeColour = isUTRInGroup(row.values._id) ? selectedBadgeColour : unselectedBadgeColour;

      const badges = [
        getBadge(
          standards[type]?.shortName ?? typeStr,
          standards[type]?.name ?? typeStr,
          badgeColour,
          `${utrId}-${type}`
        ),
      ];
      if (row.original.alternatives) {
        for (const [key] of Object.entries(row.original.alternatives)) {
          const group = getGroup('standards', key);
          if (group && key !== type) {
            badges.push(getBadge(group.shortName ?? group.name, group.name, badgeColour, `${utrId}-${key}`));
          }
        }
      }
      return <>{badges.map((badge) => badge)}</>;
    },
  });

  if (Boolean(questions.length) && !handleEditUtr) {
    tableColumns.push({
      accessor: '_id',
      Header: () => '',
      cellProps: {
        className: 'idCol order-3'
      },
      Cell: ({ value, row }) => {
        if (handleEditUtr) {
          return <></>;
        }
        if (isUTRInGroup(value)) {
          return (
            <SimpleTooltip text={`Remove Metric from group '${metricGroup?.groupName ?? 'New Group'}'`}>
              <IconButton
                disabled={isSaving}
                color='danger'
                size='xs'
                className='icon-button--size-xs'
                onClick={() => removeFromGroup(value)}
                icon='fal fa-times'
              />
            </SimpleTooltip>
          );
        } else {
          return (
            <SimpleTooltip text={`Add Metric to group '${metricGroup?.groupName ?? 'New Group'}'`}>
              <IconButton
                disabled={isSaving}
                size='xs'
                className='icon-button--size-xs'
                onClick={() => addToGroup(row.original)}
                icon='fal fa-plus'
              />
            </SimpleTooltip>
          );
        }
      },
    });
  }

  if (Boolean(questions.length) && handleEditUtr && !isReadOnly) {
    tableColumns.push({
      accessor: 'valueLabel',
      Header: () => '',
      cellProps: {
        className: 'buttonsCol flex-shrink-1 order-3'
      },
      Cell: ({ row }: { row: { original: UniversalTrackerPlain } }) => {
        return handleEditUtr ? (
          <CTAButtons
            user={user}
            initiativeId={initiativeId}
            handleEditUtr={handleEditUtr}
            universalTracker={row.original}
            handleRemoveUtr={removeFromGroup} />
        ) : null;
      },
    });
  }

  return (
    <>
      {errorMessage && <div className='alert alert-danger'>{errorMessage}</div>}
      <div className='metric-table-container'>
        <Table
          responsive={true}
          className={isSaving ? 'isSaving' : ''}
          columns={tableColumns}
          data={questions}
          pageSize={25}
        />
        {isSaving && <Loader />}
      </div>
      {!Boolean(questions.length) ? (
        <div className='metric-group-form--text-helper'>
          {helperText}
        </div>
      ) : null}
    </>
  );
};
export default MetricTable;
